/**
 * AgentReadinessCard — simplified self-view for agents (org toggle, default OFF).
 * Renders nothing when the org hasn't enabled it or no score exists yet.
 */
import { useEffect, useState } from 'react';
import { Gauge } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const STATE_LABEL = {
  floor_ready: ['Floor ready', 'bg-emerald-100 text-emerald-800'],
  on_track: ['On track', 'bg-sky-100 text-sky-800'],
  at_risk: ['Needs attention', 'bg-amber-100 text-amber-800'],
  not_ready: ['Keep going', 'bg-rose-100 text-rose-800'],
  not_scored: ['Not scored yet', 'bg-slate-100 text-slate-600'],
};

export default function AgentReadinessCard() {
  const { get } = useApi();
  const [data, setData] = useState(null);

  useEffect(() => {
    get('/readiness/me').then(setData).catch(() => setData(null));
  }, [get]);

  if (!data?.enabled || !data.available) return null;
  const [label, cls] = STATE_LABEL[data.state] || STATE_LABEL.not_scored;

  return (
    <Card className="p-4" data-testid="agent-readiness-card">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Gauge className="w-6 h-6 text-primary" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display text-lg font-semibold" data-testid="agent-readiness-score">
              Readiness: {data.score != null ? Math.round(data.score) : '—'}
            </p>
            <Badge variant="outline" className={cls} data-testid="agent-readiness-state">{label}</Badge>
            <span className="text-xs text-muted-foreground">based on {data.coverage_label} inputs</span>
          </div>
          {data.focus_areas?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="agent-readiness-focus">
              Focus on: {data.focus_areas.join(' · ')}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
